/** Client-side validation for Services requests.
 *
 * The Edge Function repeats these invariants at the trust boundary. Keeping
 * this schema in the form prevents a slow round-trip for simple omissions;
 * keeping the catalog itself shared prevents category/need drift.
 */
import { z } from 'zod'
import {
  SERVICE_CATEGORY_IDS,
  SERVICE_TIMINGS,
  SERVICE_WINDOWS,
  serviceNeedBelongsToCategory,
  serviceNeedRequiresDestination,
} from '../services/services-catalog'

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid service date')

export const serviceLocationSchema = z.object({
  label: z.string().trim().min(2).max(180),
  latitude: z.number().finite().min(-90).max(90),
  longitude: z.number().finite().min(-180).max(180),
})

export const serviceRequestInputSchema = z
  .object({
    idempotencyKey: z.string().trim().min(8).max(80),
    category: z.enum(SERVICE_CATEGORY_IDS),
    need: z.string().trim().min(1).max(64),
    location: serviceLocationSchema,
    destination: serviceLocationSchema.nullable(),
    timing: z.enum(SERVICE_TIMINGS),
    scheduledDate: isoDateSchema.nullable(),
    arrivalWindow: z.enum(SERVICE_WINDOWS),
    details: z.string().trim().min(12).max(1200),
  })
  .superRefine((value, context) => {
    if (!serviceNeedBelongsToCategory(value.category, value.need)) {
      context.addIssue({
        code: 'custom',
        path: ['need'],
        message: 'The selected need does not belong to this service.',
      })
    }
    const requiresDestination = serviceNeedRequiresDestination(value.category, value.need)
    if (requiresDestination && value.destination === null) {
      context.addIssue({
        code: 'custom',
        path: ['destination'],
        message: 'This service needs a destination.',
      })
    }
    if (!requiresDestination && value.destination !== null) {
      context.addIssue({
        code: 'custom',
        path: ['destination'],
        message: 'This service does not use a destination.',
      })
    }
    if (value.timing === 'scheduled' && !value.scheduledDate) {
      context.addIssue({
        code: 'custom',
        path: ['scheduledDate'],
        message: 'A scheduled service needs a date.',
      })
    }
    if (value.timing === 'now' && value.scheduledDate !== null) {
      context.addIssue({
        code: 'custom',
        path: ['scheduledDate'],
        message: 'An immediate service cannot carry a scheduled date.',
      })
    }
  })

export type ValidServiceRequestInput = z.infer<typeof serviceRequestInputSchema>
